import React, { useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { useDebounce } from 'usehooks-ts';

import { useIntegrationTeamsQuery } from '../../../api/integrationTeamsQuery';
import { useAuth } from '../../../app/AuthorizationProvider';
import { useIntegrations } from '../../../app/IntegrationsProvider';
import { WebexCallingTeamsRequestBodyType } from '../../../constants/integrationConstants';
import { MILLIS_500 } from '../../../constants/timeConstants';
import {
  INTEGRATION_FORM_FIELDS,
  USER_SYNCHRONIZATION_FIELDS,
  USER_SYNC_FILTER_TEAMS_OPTIONS,
} from '../../../scene/integrations/types';
import { ETL_PERMISSIONS } from '../../../utils/keycloakUtils';
import { teamSelectorProviderStateMock } from '../../../utils/test/TeamSelectorProviderMock';
import { ETL_INTEGRATION_TYPE, Values } from '../../configuration/types';

const TeamSelectorContext = React.createContext(teamSelectorProviderStateMock);

const getCorrectPermission = (integration: Values<typeof ETL_INTEGRATION_TYPE>): Values<typeof ETL_PERMISSIONS> => {
  switch (integration) {
    case ETL_INTEGRATION_TYPE.wxcalling:
      return ETL_PERMISSIONS.getTeamsWebexCalling;
    default:
      throw new Error('Received invalid integration!');
  }
};

export const TeamSelectorProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { integrationType } = useIntegrations();

  const { userHasPermissions } = useAuth();
  const [internalState, setInternalState] = useState<WebexCallingTeamsRequestBodyType>();
  const debouncedState = useDebounce(internalState, MILLIS_500);
  const [isDebounceInProgress, setIsDebounceInProgress] = useState(true);

  const {
    input: { value: clientId },
    meta: { valid: isClientIdValid },
  } = useField(INTEGRATION_FORM_FIELDS.id(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: organizationId },
    meta: { valid: isOrganizationIdValid },
  } = useField(INTEGRATION_FORM_FIELDS.organizationId, { subscription: { value: true, valid: true } });

  const {
    input: { value: clientSecret },
    meta: { valid: isClientSecretValid },
  } = useField(INTEGRATION_FORM_FIELDS.secret(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: apiUrl },
    meta: { valid: isApiUrlValid },
  } = useField(INTEGRATION_FORM_FIELDS.apiUrl(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: accessToken },
    meta: { valid: isAccessTokenValid },
  } = useField(INTEGRATION_FORM_FIELDS.accessToken(integrationType), { subscription: { value: true, valid: true } });

  const {
    input: { value: selectedTeams },
  } = useField<string[]>(USER_SYNCHRONIZATION_FIELDS.selectedTeams, {
    subscription: {
      value: true,
    },
  });

  const {
    input: { value: teamSelectorMode },
  } = useField<(typeof USER_SYNC_FILTER_TEAMS_OPTIONS)[keyof typeof USER_SYNC_FILTER_TEAMS_OPTIONS]>(
    USER_SYNCHRONIZATION_FIELDS.teamSelectorMode,
    {
      subscription: {
        value: true,
      },
    }
  );

  useEffect(() => {
    setIsDebounceInProgress(false);
  }, [debouncedState]);

  useEffect(() => {
    const isDefined = !!(clientId && clientSecret && organizationId && apiUrl && accessToken);
    const isValid =
      isClientIdValid && isClientSecretValid && isOrganizationIdValid && isApiUrlValid && isAccessTokenValid;

    if (!isDefined || !isValid) {
      setInternalState(undefined);
      return;
    }

    setInternalState({ accessToken, apiUrl });
    setIsDebounceInProgress(true);
  }, [
    clientId,
    clientSecret,
    organizationId,
    apiUrl,
    isClientIdValid,
    isClientSecretValid,
    isOrganizationIdValid,
    isApiUrlValid,
    accessToken,
    isAccessTokenValid,
  ]);

  const permissionToCheck = useMemo(() => getCorrectPermission(integrationType), [integrationType]);
  const isPermittedForIntegrationTeams = userHasPermissions([permissionToCheck]);
  const {
    data: integrationTeams,
    isError: isIntegrationTeamsError,
    isLoading: isIntegrationTeamsLoading,
  } = useIntegrationTeamsQuery(integrationType, debouncedState, {
    enabled: isPermittedForIntegrationTeams,
    retry: 1, // prevent to make a lot of requests
    refetchOnWindowFocus: false
  });

  const isDefined = !!(clientId && clientSecret && organizationId && apiUrl && accessToken);
  const isValid =
    isClientIdValid && isClientSecretValid && isOrganizationIdValid && isApiUrlValid && isAccessTokenValid;
  const value = {
    teamOptions: integrationTeams ?? [],
    isIntegrationTeamsError,
    isIntegrationTeamsLoading: isDebounceInProgress || isIntegrationTeamsLoading,
    isIntegrationDataFilled: !!isValid && isDefined,
    isPermittedForIntegrationTeams,
    teamSelectorMode,
    selectedTeams,
  };

  return <TeamSelectorContext.Provider value={value}>{children}</TeamSelectorContext.Provider>;
};

export const IntegrationTeamsConsumer = TeamSelectorContext.Consumer;

export const useIntegrationTeams = () => React.useContext(TeamSelectorContext);
